import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products', hasDropdown: true },
    { name: 'About Us', path: '/about' },
    { name: 'Articles', path: '/articles' },
    { name: 'Contact', path: '/contact' },
  ];

  const productSubMenu = [
    { name: 'Kesar (Saffron)', path: '/products/kesar' }
  ];

  const isActive = (path) => location.pathname === path;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProductsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="https://customer-assets.emergentagent.com/job_harvest-india/artifacts/sohp50hk_hindharvest_green_Transparent.png" 
              alt="Hind Harvest Logo" 
              className="h-20 w-20 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <span className="text-2xl font-bold text-gray-800">Hind Harvest</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              item.hasDropdown ? (
                <div
                  key={item.name}
                  className="relative"
                  ref={dropdownRef}
                >
                  <button
                    onClick={() => setIsProductsOpen(!isProductsOpen)}
                    className={`text-sm font-medium transition-colors duration-200 relative pb-1 flex items-center gap-1 ${
                      isActive(item.path) || location.pathname.startsWith('/products/')
                        ? 'text-saffron-600'
                        : 'text-gray-700 hover:text-saffron-600'
                    }`}
                  >
                    {item.name}
                    <ChevronDown size={16} className={`transition-transform duration-200 ${isProductsOpen ? 'rotate-180' : ''}`} />
                    {(isActive(item.path) || location.pathname.startsWith('/products/')) && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-saffron-600"></span>
                    )}
                  </button>
                  
                  {/* Dropdown Menu */}
                  {isProductsOpen && (
                    <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                      {productSubMenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          onClick={() => setIsProductsOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-saffron-50 hover:text-saffron-600 transition-colors duration-200"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-sm font-medium transition-colors duration-200 relative pb-1 ${
                    isActive(item.path)
                      ? 'text-saffron-600'
                      : 'text-gray-700 hover:text-saffron-600'
                  }`}
                >
                  {item.name}
                  {isActive(item.path) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-saffron-600"></span>
                  )}
                </Link>
              )
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-3 px-4 text-sm font-medium transition-colors duration-200 ${
                  isActive(item.path)
                    ? 'text-saffron-600 bg-saffron-50'
                    : 'text-gray-700 hover:text-saffron-600 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;